import {
  Button,
  Grid,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {
  Call,
  Email,
  Face,
  Sms,
  MailOutline,
  Print,
  Computer,
  Comment,
  Dehaze,
} from '@mui/icons-material';

import SearchIcon from '@mui/icons-material/Search';
import { fetchServiceGroups, fetchUsers } from 'actions/action-lookups';
import {
  addNote,
  fetchNotesSidebar,
  togglePinnedNote,
  updateNoteBar,
  filterNotesSidebar,
  fetchArchivedNotes,
  setNotesView,
} from 'actions/action-notes';
import { noteTagTypes } from 'constants/lists';
import { LookupNoteTagType } from 'constants/enums';

import ConfirmationPanel from 'components/form/confirmation/confirmation-panel';
import { EditClose, MoreNotesIcon, ArchivedNoteIcon } from 'components/icons/icons';
import TherapyDropDownSelector from 'components/therapy-selector/therapy-drop-down-selector';
import { ReactComponent as FilterIcon } from 'lib/logos/Filter.svg';
import { ADD_NOTE_FORM, NOTE_PAGE_LIMIT } from 'constants/index';
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { bindActionCreators } from 'redux';
import { Field, formValueSelector } from 'redux-form';
import { Loader } from 'components/loading-overlay/loading-overlay';
import { translateUsers } from 'services/utils/users-service';
import { parseNote, parseNoteToDb, getNoteAddAndPinTags } from 'services/utils/note-service';
import { Notes } from 'constants/GA';
import { logEvent } from 'services/utils/analytics';
import createMemoReduxForm from 'utils/create-memo-redux-form';
import NoteDetail from './note-detail';
import NoteTreeView from './note-tree-view';
import { styles } from './note-styles';
import NoteArea from './NoteArea';

const NoteFilterOptions = {
  ALL: {
    id: 'ALL',
    display: 'All Types',
    icon: <Dehaze />,
  },
  NOTES: {
    id: 'NOTES',
    display: 'Notes',
    icon: <Comment />,
  },
  PHONE: {
    id: 'PHONE',
    display: 'Phone',
    icon: <Call />,
    communication_type_id: 4,
  },
  IN_PERSON: {
    id: 'IN_PERSON',
    display: 'In Person',
    icon: <Face />,
    communication_type_id: 1,
  },
  EMAIL: {
    id: 'EMAIL',
    display: 'Email',
    icon: <Email />,
    communication_type_id: 2,
  },
  SMS: {
    id: 'SMS',
    display: 'SMS',
    icon: <Sms />,
    communication_type_id: 3,
  },
  LETTER: {
    id: 'LETTER',
    display: 'Letter',
    icon: <MailOutline />,
    communication_type_id: 5,
  },
  FAX: {
    id: 'FAX',
    display: 'Fax',
    icon: <Print />,
    communication_type_id: 6,
  },
  EHR: {
    id: 'EHR',
    display: 'EHR',
    icon: <Computer />,
    communication_type_id: 7,
  },
};

const NoteBar = props => {
  const {
    userPreferencesShowTreeView,
    reset,
    setNotesView,
    fetchArchivedNotes,
    noteBar,
    fetchNotesSidebar,
    updateNoteBar,
    filterNotesSidebar,
    existingTasks,
    existingTherapies,
    selectedPatientId,
    addNote,
    togglePinnedNote,
    closeHandler,
    classes,
    pristine,
    notes,
    users,
    serviceGroups,
    archivedNotes,
    allNotes,
    handleSubmit,
    noteText,
  } = props;

  const {
    tagNoteLoadedNum,
    allNoteLoadedNum,
    tagTypeId,
    tagResourceId,
    showAllNotes,
    therapy,
    allNotesLoaded,
    tag,
    loadingNotes,
    loadingMoreNotes,
    tagNotesReachEnd,
    allNotesReachEnd,
    tagResourceIds = [],
    order_fc_ids = [],
    therapy_ids = [],
    selected_therapies = [],
  } = noteBar;

  const [showArchived, setShowArchived] = useState(false);
  const [showTreeView, setShowTreeView] = useState(userPreferencesShowTreeView);
  const [openFilter, setOpenFilter] = useState(false);
  const [_filterAnchorEl, setFilterAnchorEl] = useState(false);
  const [filters, setFilters] = useState([NoteFilterOptions.ALL.id]);
  const [searchString, setSearchString] = useState(false);
  const isSubmittingNoteRef = useRef(false);

  useEffect(() => {
    if (!showArchived) {
      filterNotesSidebar({
        patient_id: selectedPatientId,
        tag_type_id: props.tagTypeId,
        tag_resource_id: props.tagResourceId,
        therapy_id: tagTypeId === 2 ? props.tagResourceId : null,
        showAllNotes: false,
      });
    }
    reset();
  }, []);

  const handleFilterToggle = value => {
    const currentIndex = filters.indexOf(value);
    let newChecked = [...filters];

    if (currentIndex === -1) {
      if (value === NoteFilterOptions.ALL.id) {
        newChecked = [NoteFilterOptions.ALL.id];
      } else {
        newChecked.push(value);
        newChecked = newChecked.filter(id => id !== NoteFilterOptions.ALL.id);
      }
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setFilters(newChecked);
  };

  const toggleFilter = e => {
    setOpenFilter(!openFilter);
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilter = () => {
    setOpenFilter(false);
  };

  const handleShowTreeSwitch = () => {
    logEvent(
      Notes.category,
      Notes.actions.switch_notes_view,
      !showTreeView ? Notes.labels.tree_view : Notes.labels.date_view,
    );
    setNotesView(!showTreeView);
    setShowTreeView(!showTreeView);
  };

  const handleShowArchived = () => {
    fetchArchivedNotes({
      patient_id: selectedPatientId,
      group_by_tag: true,
      archive: true,
    });
    setShowArchived(!showArchived);
  };

  const loadNextPage = () => {
    updateNoteBar({
      ...noteBar,
      loadingMoreNotes: true,
    });
    if (showAllNotes) {
      fetchNotesSidebar(
        {
          patient_id: selectedPatientId,
          limit: NOTE_PAGE_LIMIT + 1, // return 1 more note to check if loaded all
          offset: allNoteLoadedNum,
        },
        existingTasks,
      ).then(action => {
        if (!showArchived) {
          filterNotesSidebar({
            patient_id: selectedPatientId,
            tag_type_id: tagTypeId,
            tag_resource_id: tagResourceId,
            therapy_id: therapy ? therapy.id : null,
          });
        }
        const noteArr =
          action && action.payload && action.payload.data ? action.payload.data.notes : []; // eslint-disable-line
        if (noteArr.length < NOTE_PAGE_LIMIT + 1) {
          // return notes less than NOTE_PAGE_LIMIT + 1 means we already
          // loaded all notes for this tag, set tagNotesReachEnd to true
          updateNoteBar({
            ...noteBar,
            allNoteLoadedNum: allNoteLoadedNum + noteArr.length,
            allNotesReachEnd: true,
            loadingMoreNotes: false,
          });
        } else {
          updateNoteBar({
            ...noteBar,
            allNoteLoadedNum: allNoteLoadedNum + NOTE_PAGE_LIMIT,
            loadingMoreNotes: false,
          });
        }
      });
    } else {
      fetchNotesSidebar(
        {
          patient_id: selectedPatientId,
          tag_type_id: tagTypeId,
          tag_resource_id: tagResourceId,
          limit: NOTE_PAGE_LIMIT + 1,
          offset: tagNoteLoadedNum,
        },
        existingTasks,
      ).then(action => {
        const noteArr =
          action && action.payload && action.payload.data ? action.payload.data.notes : []; // eslint-disable-line
        if (noteArr.length < NOTE_PAGE_LIMIT + 1) {
          updateNoteBar({
            ...noteBar,
            tagNoteLoadedNum: tagNoteLoadedNum + noteArr.length,
            tagNotesReachEnd: true,
          });
        } else {
          updateNoteBar({
            ...noteBar,
            tagNoteLoadedNum: tagNoteLoadedNum + NOTE_PAGE_LIMIT,
          });
        }
      });
    }
  };

  const toggleShowAllNotes = () => {
    if (!allNotesLoaded) {
      updateNoteBar({
        ...noteBar,
        loadingNotes: true,
      });
      fetchNotesSidebar(
        {
          patient_id: selectedPatientId,
          limit: NOTE_PAGE_LIMIT + 1,
        },
        existingTasks,
      ).then(action => {
        const noteArr =
          action && action.payload && action.payload.data ? action.payload.data.notes : []; // eslint-disable-line
        if (noteArr.length < NOTE_PAGE_LIMIT + 1) {
          updateNoteBar({
            ...noteBar,
            showAllNotes: !noteBar.showAllNotes,
            allNoteLoadedNum: allNoteLoadedNum ? allNoteLoadedNum + noteArr.length : noteArr.length,
            loadingNotes: false,
            allNotesLoaded: true,
            allNotesReachEnd: true,
          });
        } else {
          updateNoteBar({
            ...noteBar,
            showAllNotes: !noteBar.showAllNotes,
            allNoteLoadedNum: allNoteLoadedNum
              ? allNoteLoadedNum + NOTE_PAGE_LIMIT
              : NOTE_PAGE_LIMIT,
            loadingNotes: false,
            allNotesLoaded: true,
          });
        }

        if (!showArchived) {
          filterNotesSidebar({
            patient_id: selectedPatientId,
            tag_type_id: tagTypeId,
            tag_resource_id: tagResourceId,
            therapy_id: tagTypeId === 2 ? tagResourceId : null,
            showAllNotes: !showAllNotes,
          });
        }
      });
    } else {
      updateNoteBar({
        ...noteBar,
        showAllNotes: !noteBar.showAllNotes,
      });

      if (!showArchived) {
        filterNotesSidebar({
          patient_id: selectedPatientId,
          tag_type_id: tagTypeId,
          tag_resource_id: tagResourceId,
          therapy_id: tagTypeId === 2 ? tagResourceId : null,
          showAllNotes: !showAllNotes,
        });
      }
    }
  };

  const onSubmit = async values => {
    if (isSubmittingNoteRef.current) {
      return;
    }
    isSubmittingNoteRef.current = true;
    const newValues = parseNoteToDb(values);
    const { tagTypeId, tagResourceId, tagNoteLoadedNum, allNoteLoadedNum, showAllNotes, therapy } =
      noteBar;

    const noteTask = existingTasks
      ? existingTasks.find(
          t => LookupNoteTagType[t.taskType] === tagTypeId && t.id === tagResourceId,
        )
      : null;

    const noteTaskTherapyId = noteTask ? noteTask.therapy_id : null;

    const buildNotePayload = noteTags => ({
      patient_id: selectedPatientId,
      note_text: newValues.note_text,
      is_pinned: 0,
      mentions: newValues.mentions,
      tags: noteTags,
    });

    const updateNoteBarCounts = showAll => {
      if (showAll) {
        updateNoteBar({
          ...noteBar,
          allNoteLoadedNum: allNoteLoadedNum ? allNoteLoadedNum + 1 : 1,
          ...(showAllNotes ? {} : { showAllNotes: true }),
        });
      } else {
        updateNoteBar({
          ...noteBar,
          tagNoteLoadedNum: tagNoteLoadedNum ? tagNoteLoadedNum + 1 : 1,
        });
      }
    };

    try {
      // For Order bundles with multiple therapies, create one note per selected FC.
      // Each FC in the bundle represents a unique therapy and needs its own note entry.
      if (tagResourceIds && tagResourceIds.length > 0) {
        const targetTherapies =
          selected_therapies.length === 1 && selected_therapies[0].value === -1
            ? Object.values(existingTherapies).filter(therapy => therapy_ids.includes(therapy.id))
            : selected_therapies
                .map(option => existingTherapies.find(therapy => therapy.id === option.value))
                .filter(Boolean);
        const targetTherapyIds = targetTherapies.map(option => option.id);
        const targetFCs = (existingTasks || []).filter(
          task => task.taskType === 'FC' && targetTherapyIds.includes(task.therapy_id),
        );
        for (const task of targetFCs) {
          const addtl_tags = getNoteAddAndPinTags(
            values.note_pin,
            tagTypeId,
            task.id,
            task.therapy_id,
            selectedPatientId,
          );
          const payload = buildNotePayload(addtl_tags);
          const response = await addNote(payload); // eslint-disable-line no-await-in-loop
          if (response && response.payload && response.payload.data) {
            updateNoteBarCounts(showAllNotes || targetFCs.length > 1);
          }
        }
        reset();
      } else {
        const therapyId = noteTaskTherapyId;
        const new_tags = getNoteAddAndPinTags(
          values.note_pin,
          tagTypeId,
          tagResourceId,
          Number(tagTypeId) === 2 ? tagResourceId : therapyId,
          selectedPatientId,
        );
        const payload = buildNotePayload(new_tags);

        const response = await addNote(payload);
        if (response && response.payload && response.payload.data) {
          updateNoteBarCounts(showAllNotes);
        }
        reset();
      }
    } finally {
      isSubmittingNoteRef.current = false;
    }
  };

  const handleCancel = () => {
    reset();
  };

  const handleTogglePin = note => {
    togglePinnedNote(note);
  };

  const filterNotes = notes => {
    return notes.filter(note => {
      let show = false;
      filters.forEach(filter => {
        if (filter === NoteFilterOptions.ALL.id) {
          show = true;
        } else if (filter === NoteFilterOptions.NOTES.id) {
          if (note.communication === undefined) {
            show = true;
          }
        } else if (
          note.communication &&
          note.communication.type_id === NoteFilterOptions[filter].communication_type_id
        ) {
          show = true;
        }
        if (searchString) {
          show = note.note_text.toLowerCase().includes(searchString.toLowerCase());
        }
      });
      return show;
    });
  };

  const handleNotesSearch = e => {
    const allNotesLoaded = showAllNotes ? allNotesReachEnd : tagNotesReachEnd;
    if (e.target.value.length > 2) {
      if (!allNotesLoaded) {
        loadNextPage();
      }
    }
    if (e.target.value.length > 2) {
      setSearchString(e.target.value);
    } else {
      setSearchString(null);
    }
  };

  const renderWrapper = children => {
    return (
      <div className={classes.noteWrapper} data-qa-id="notes-panel">
        {children}
      </div>
    );
  };

  const userData = users && users.length > 0 && translateUsers(users);

  const tagStr = `#${tag}`;
  // eslint-disable-next-line
  const displayMoreBtn =
    (!showAllNotes && !tagNotesReachEnd) || (showAllNotes && !allNotesReachEnd);

  return renderWrapper(
    <>
      <div className={classes.noteEntryArea}>
        <Grid container className={classes.noteBarTop}>
          <Grid item xs={2}>
            <Typography className={classes.noteTitle} data-qa-id="notes-panel-title">
              Notes
            </Typography>
          </Grid>
          <Grid item xs={2} />
          <Grid item xs={8}>
            {!showTreeView && (
              <Grid container alignItems="center" justifyContent="flex-end">
                <Button className={classes.noteBtn} variant="outlined" onClick={toggleShowAllNotes}>
                  <Typography className={classes.allNotesButtonText}>
                    {showAllNotes ? tagStr : 'All Notes'}
                  </Typography>
                </Button>
                <Button
                  id="filterPanelButton"
                  name="filter_panel_button"
                  variant="outlined"
                  size="small"
                  onClick={e => toggleFilter(e)}
                  className={classes.noteBtn}
                >
                  <FilterIcon />
                </Button>
                <Button id="note_close" className={classes.closeBtn} onClick={closeHandler}>
                  <EditClose />
                </Button>
              </Grid>
            )}
          </Grid>
          <Divider />
          {openFilter && (
            <List className={classes.noteFilterContainer}>
              {Object.values(NoteFilterOptions).map(option => {
                const { id, display, icon } = option;
                const labelId = `note-filter-list-label-${id}`;
                return (
                  <ListItem key={id} dense button onClick={() => handleFilterToggle(id)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={filters.indexOf(id) !== -1}
                        tabIndex={-1}
                        color="primary"
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                      <div className={classes.noteFilterIcon}>{icon}</div>
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={display} />
                  </ListItem>
                );
              })}
            </List>
          )}
          {!showArchived && !openFilter && !showTreeView && (
            <Grid item xs={12} className={classes.noteDisplay}>
              <Grid container alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search Notes"
                  variant="standard"
                  onChange={handleNotesSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  id="outlined-basic"
                />
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            {order_fc_ids.length > 1 ? (
              <TherapyDropDownSelector
                classes={classes}
                id="note_target_therapy"
                selectorText="Select target therapies for this note"
              />
            ) : null}
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <Grid item xs={12} className={classes.noteFieldContainer}>
                <Typography
                  className={classes.tag}
                  component="span"
                  data-qa-id="notes-panel-subtitle"
                >
                  {tagStr}
                </Typography>
                <Field
                  name="note_text"
                  data={userData}
                  className={classes.fieldNoteText}
                  autoFocus
                  component={NoteArea}
                  formId={ADD_NOTE_FORM}
                />
              </Grid>
              <Grid item xs={12} className={classes.confirmationPanelContainer}>
                <ConfirmationPanel
                  handleCancel={handleCancel}
                  disableSubmit={pristine || !noteText || noteText.length === 0}
                  onSubmit={onSubmit}
                />
              </Grid>
            </form>
          </Grid>
          <Divider />
          <Grid item xs={12}>
            <Grid
              container
              alignItems="center"
              justifyContent="flex-end"
              className={classes.treeSwitchContainer}
            >
              <Typography className={classes.tag} component="span">
                Tree
              </Typography>
              <Switch
                checked={!showTreeView}
                onChange={handleShowTreeSwitch}
                value="showTree"
                color="primary"
              />
              <Typography className={classes.tag} component="span">
                Date
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <div className={classes.noteBar}>
          {!!showArchived && (
            <Grid item xs={12} className={classes.noteDisplay}>
              <Grid container alignItems="center">
                <ArchivedNoteIcon inlineArchivedTop />
                <Typography
                  variant="caption"
                  component="span"
                  className={classes.noteArchivedTextTop}
                >
                  Archived Notes
                </Typography>
              </Grid>
            </Grid>
          )}
        </div>
      </div>
      <div className={classes.loadContainer}>
        <Loader loaded={users.length > 0 && serviceGroups.length > 0 && !loadingNotes}>
          {!showTreeView ? (
            <div>
              {showAllNotes ? (
                <div>
                  {showArchived ? (
                    <div>
                      {archivedNotes.map(note => {
                        if (note.is_archived) {
                          return (
                            <div>
                              <NoteDetail
                                key={note.id}
                                toggle={handleTogglePin}
                                note={parseNote(note, users, serviceGroups)}
                                classes={classes}
                                isAllNotes
                              />
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  ) : (
                    <div>
                      {filterNotes(allNotes).map(note => {
                        if (!note.is_archived) {
                          return (
                            <div>
                              <NoteDetail
                                key={note.id}
                                toggle={handleTogglePin}
                                note={parseNote(note, users, serviceGroups)}
                                classes={classes}
                                isAllNotes
                              />
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {showAllNotes ? (
                    <div>
                      {showArchived ? (
                        <div>
                          {archivedNotes.map(note => (
                            <div>
                              <NoteDetail
                                key={note.id}
                                toggle={handleTogglePin}
                                note={parseNote(note, users, serviceGroups)}
                                classes={classes}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                          {notes.map(note => (
                            <div>
                              <NoteDetail
                                key={note.id}
                                toggle={handleTogglePin}
                                note={parseNote(note, users, serviceGroups)}
                                classes={classes}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      {!showArchived ? (
                        <div>
                          {filterNotes(notes).map(note => {
                            if (!note.is_archived) {
                              return (
                                <div key={note.id}>
                                  <NoteDetail
                                    key={note.id}
                                    toggle={handleTogglePin}
                                    note={parseNote(note, users, serviceGroups)}
                                    classes={classes}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      ) : (
                        <div>
                          {archivedNotes.map(note => {
                            if (note.is_archived) {
                              return (
                                <div key={note.id}>
                                  <NoteDetail
                                    key={note.id}
                                    toggle={handleTogglePin}
                                    note={parseNote(note, users, serviceGroups)}
                                    classes={classes}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              {showArchived ? (
                <div>
                  {archivedNotes.map(note => {
                    if (note.is_archived) {
                      return (
                        <div>
                          <NoteDetail
                            key={note.id}
                            toggle={handleTogglePin}
                            note={parseNote(note, users, serviceGroups)}
                            classes={classes}
                            isAllNotes
                          />
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              ) : (
                <NoteTreeView />
              )}
            </div>
          )}

          {displayMoreBtn && (
            <div className={classes.loadMoreContainer}>
              <Loader loaded={users.length > 0 && serviceGroups.length > 0 && !loadingMoreNotes}>
                <Grid item xs={12} onClick={loadNextPage} className={classes.moreNotesBtnContainer}>
                  <Typography component="span">More Notes</Typography>
                  <MoreNotesIcon />
                </Grid>
              </Loader>
            </div>
          )}
          <Grid item xs={12} onClick={loadNextPage} className={classes.archivedNoteButtonContainer}>
            <Grid container alignItems="center" justifyContent="flex-end">
              <Button
                className={classes.noteArchiveBtn}
                variant="outlined"
                onClick={handleShowArchived}
              >
                {!showArchived && <ArchivedNoteIcon />}
                <Typography className={classes.noteArchivedText}>
                  {!showArchived ? 'Show Archived Notes' : 'Show Notes'}
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </Loader>
      </div>
    </>,
  );
};

function mapStateToProps(state) {
  const { notes, noteBar, patient, selectedPatientId, tasks, therapies } = state;
  const selector = formValueSelector(ADD_NOTE_FORM);
  const noteText = selector(state, 'note_text');

  const existingTasks = Object.values(state.tasks?.data || {});
  const existingTherapies = Object.values(state.therapies?.data || {});

  const notesArr = notes && notes.notes ? notes.notes.notesToDisplay : [];
  const allNotesArr =
    notes && notes.notes ? notes.notes.allPinnedNotes.concat(notes.notes.allNonPinnedNotes) : [];

  const filterNotesArr = arr => {
    if (
      arr.some(note =>
        note.tags.some(
          tag =>
            tag.is_all_of_type === 1 &&
            tag.is_all_of_type_category === 2 &&
            tag.artificial_therapy_ids &&
            tag.artificial_therapy_ids.length > 0 &&
            noteBar.tagResourceId,
        ),
      )
    ) {
      return arr.filter(note => {
        const noteTaskType =
          noteTagTypes && noteTagTypes.find(tagType => tagType.value === note.tags[0].tag_type_id)
            ? noteTagTypes.find(tagType => tagType.value === note.tags[0].tag_type_id).label
            : null;
        const noteTask = tasks.data[`${noteTaskType}${noteBar.tagResourceId}`];
        if (
          noteTask &&
          noteTask.therapy_id &&
          note &&
          note.tags &&
          note.tags.some(tag => tag.artificial_therapy_ids) &&
          note.tags.some(tag => !tag.artificial_therapy_ids.some(t => t === noteTask.therapy_id))
        ) {
          return false;
        }
        return true;
      });
    }
    return arr;
  };
  return {
    patient,
    selectedPatientId,
    notes: filterNotesArr(notesArr),
    allNotes: allNotesArr,
    archivedNotes: notes && notes.notes ? notes.notes.archivedNotes : [],
    noteBar,
    tasks,
    users: state.lookups.users,
    serviceGroups: state.lookups.serviceGroups,
    userPreferencesShowTreeView: state.userPreferences.show_tree_view,
    existingTasks,
    existingTherapies,
    noteText,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchNotesSidebar,
      filterNotesSidebar,
      fetchUsers,
      addNote,
      fetchServiceGroups,
      togglePinnedNote,
      updateNoteBar,
      fetchArchivedNotes,
      setNotesView,
    },
    dispatch,
  );
}

NoteBar.displayName = 'NoteBar';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
  createMemoReduxForm({ form: ADD_NOTE_FORM }),
)(NoteBar);
